/**
 * Firebase Configuration
 * Initialize Firebase with environment variables
 */

import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, doc, query, where, orderBy, limit, onSnapshot, getDocs, Timestamp } from 'firebase/firestore';
import { getDatabase, ref, onValue } from 'firebase/database';

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const rtdb = getDatabase(app);

/**
 * Sign in with email and password
 */
export async function loginWithEmail(email, password) {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return { success: true, user: userCredential.user };
    } catch (error) {
        console.error('Login error:', error);
        return { success: false, error: 'Incorrect email or password.' };
    }
}

/**
 * Sign out current user
 */
export async function logout() {
    try {
        await signOut(auth);
        return { success: true };
    } catch (error) {
        console.error('Logout error:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get current user's ID token
 */
export async function getIdToken() {
    const user = auth.currentUser;
    if (!user) return null;

    try {
        return await user.getIdToken(true);
    } catch (error) {
        console.error('Error getting ID token:', error);
        return null;
    }
}

/**
 * Get current user
 */
export function getCurrentUser() {
    return auth.currentUser;
}

/**
 * Listen for auth state changes
 */
export function onAuthChange(callback) {
    return onAuthStateChanged(auth, callback);
}

/**
 * Subscribe to realtime location updates for a user
 * Returns an unsubscribe function
 */
export function subscribeToUserLocations(userId, onUpdate, onError) {
    try {
        const locationsRef = collection(db, 'locations');
        const q = query(
            locationsRef,
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(100)
        );

        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const locations = [];
                snapshot.forEach((doc) => {
                    const data = doc.data();

                    // Handle different timestamp formats
                    let timestamp = data.createdAt || data.uploadedAt || data.timestamp;
                    if (timestamp?.toDate) {
                        timestamp = timestamp.toDate();
                    } else if (timestamp?._seconds) {
                        timestamp = new Date(timestamp._seconds * 1000);
                    } else if (typeof timestamp === 'number') {
                        timestamp = new Date(timestamp);
                    }

                    locations.push({
                        id: doc.id,
                        ...data,
                        timestamp: timestamp || new Date()
                    });
                });
                // Sort by timestamp ascending for display
                locations.sort((a, b) => a.timestamp - b.timestamp);
                onUpdate(locations);
            },
            (error) => {
                console.error('Realtime location listener error:', error);
                if (onError) onError(error);
            }
        );

        return unsubscribe;
    } catch (error) {
        console.error('Failed to set up location listener:', error);
        if (onError) onError(error);
        return () => {}; // Return empty function
    }
}

/**
 * Subscribe to the latest location only (for live tracking)
 * Uses createdAt field which is what the Android app stores
 */
export function subscribeToLatestLocation(userId, onUpdate, onError) {
    try {
        const locationsRef = collection(db, 'locations');
        const q = query(
            locationsRef,
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(1)
        );

        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                if (!snapshot.empty) {
                    const doc = snapshot.docs[0];
                    const data = doc.data();

                    // Handle different timestamp formats from the backend
                    let timestamp = data.createdAt || data.uploadedAt || data.timestamp;
                    if (timestamp?.toDate) {
                        timestamp = timestamp.toDate();
                    } else if (timestamp?._seconds) {
                        timestamp = new Date(timestamp._seconds * 1000);
                    } else if (typeof timestamp === 'number') {
                        timestamp = new Date(timestamp);
                    }

                    onUpdate({
                        id: doc.id,
                        ...data,
                        timestamp: timestamp || new Date()
                    });
                }
            },
            (error) => {
                console.error('Realtime latest location listener error:', error);
                if (onError) onError(error);
            }
        );

        return unsubscribe;
    } catch (error) {
        console.error('Failed to set up latest location listener:', error);
        if (onError) onError(error);
        return () => {};
    }
}

/**
 * Subscribe to a user's PRESENCE in Firebase Realtime Database.
 * This is the authoritative online/offline signal — written by the Android app
 * and updated server-side by Firebase onDisconnect().
 * Returns: { online: boolean, lastSeen: number|null }
 */
export function subscribeToPresence(userId, onUpdate, onError) {
    try {
        const presenceRef = ref(rtdb, `presence/${userId}`);

        // onValue in Firebase v9 modular SDK returns an unsubscribe function directly
        const unsubscribe = onValue(presenceRef,
            (snapshot) => {
                const data = snapshot.val();
                onUpdate({
                    online: data ? data.online === true : false,
                    lastSeen: data ? data.lastSeen : null
                });
            },
            (error) => {
                console.error('Realtime presence listener error:', error);
                if (onError) onError(error);
            }
        );

        // Return unsubscribe function
        return unsubscribe;
    } catch (error) {
        console.error('Failed to set up presence listener:', error);
        if (onError) onError(error);
        return () => {};
    }
}

/**
 * Subscribe to device status details from Firestore (locationTrackingSettings).
 * This provides detailed device info (battery, permissions, tracking state, etc.)
 * written by the Android app. Display-only — NO staleness logic, NO recovery trigger.
 */
export function subscribeToDeviceStatus(userId, onUpdate, onError) {
    try {
        const docRef = doc(db, 'locationTrackingSettings', userId);

        const unsubscribe = onSnapshot(docRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data();

                    const processTimestamp = (ts) => {
                        if (!ts) return null;
                        if (ts?.toDate) return ts.toDate();
                        if (ts?._seconds) return new Date(ts._seconds * 1000);
                        if (typeof ts === 'number') return new Date(ts);
                        return ts;
                    };

                    const deviceInfo = data.lastDeviceInfo || {};
                    const trackingState = data.trackingState || data.lastDeviceState || 'unknown';
                    const serviceRunning = data.serviceRunning === true || data.lastServiceState === true;

                    onUpdate({
                        userId,
                        trackingState,
                        trackingStateMessage: data.trackingStateMessage || '',
                        isActive: data.isActive === true,

                        // Service state
                        serviceRunning,
                        lastHeartbeat: data.lastHeartbeat || 0,
                        resurrectionCount: data.resurrectionCount || 0,

                        // Permissions
                        hasFineLocation: data.hasFineLocation === true ? true : (data.hasFineLocation === false ? false : null),
                        hasBackgroundLocation: data.hasBackgroundLocation === true ? true : (data.hasBackgroundLocation === false ? false : null),
                        locationServicesEnabled: data.locationServicesEnabled === true ? true : (data.locationServicesEnabled === false ? false : null),
                        canTrackLocation: data.canTrackLocation === true,
                        missingPermission: data.missingPermission || 'none',

                        // Device state
                        batteryLevel: data.batteryLevel ?? -1,
                        batteryCharging: data.batteryCharging === true,
                        batteryStatus: data.batteryStatus || getBatteryStatus(data.batteryLevel),
                        networkConnected: data.networkConnected === true ? true : (data.networkConnected === false ? false : null),
                        networkType: data.networkType || 'unknown',

                        // Device info
                        deviceModel: data.deviceModel || deviceInfo.model || 'Unknown',
                        deviceManufacturer: data.deviceManufacturer || deviceInfo.manufacturer || 'Unknown',
                        deviceName: data.deviceName || deviceInfo.deviceName || `${deviceInfo.manufacturer || 'Unknown'} ${deviceInfo.model || 'Device'}`,
                        androidVersion: data.androidVersion || deviceInfo.androidRelease || 'Unknown',
                        sdkVersion: data.sdkVersion || deviceInfo.androidVersion || 0,
                        appVersion: data.appVersion || deviceInfo.appVersion || 'Unknown',

                        // Statistics
                        uploadSuccessRate: data.uploadSuccessRate ?? 100,
                        fcmResponseRate: data.fcmResponseRate ?? 100,
                        locationCount24h: data.locationCount24h || 0,
                        lastLocationTime: data.lastLocationTime || 0,

                        // Last status
                        lastStatusType: data.lastStatusType || '',
                        lastStatusMessage: data.lastStatusMessage || '',
                        lastFailureReason: data.lastFailureReason || null,

                        // Timestamps
                        updatedAt: processTimestamp(data.lastResponseTime || data.updatedAt),
                        updatedAtClient: data.updatedAtClient || 0
                    });
                } else {
                    onUpdate(null);
                }
            },
            (error) => {
                console.error('Realtime device status listener error:', error);
                if (onError) onError(error);
            }
        );

        return unsubscribe;
    } catch (error) {
        console.error('Failed to set up device status listener:', error);
        if (onError) onError(error);
        return () => {};
    }
}

/**
 * Subscribe to recovery status from Firestore (fcmRecoveryJobs).
 * The backend's recovery scheduler writes here. Display-only.
 */
export function subscribeToRecoveryStatus(userId, onUpdate, onError) {
    try {
        const docRef = doc(db, 'fcmRecoveryJobs', userId);

        const unsubscribe = onSnapshot(docRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data();
                    onUpdate({
                        active: data.status === 'recovering',
                        status: data.status || 'none',
                        currentStep: data.currentStep || 0,
                        currentCycle: data.currentCycle || 0,
                        totalFcmsSent: data.totalFcmsSent || 0,
                        startedAt: data.startedAt || null,
                        lastFcmSentAt: data.lastFcmSentAt || null,
                        nextFireAt: data.nextFireAt || null,
                        triggeredBy: data.triggeredBy || 'unknown',
                        cancelledAt: data.cancelledAt || null
                    });
                } else {
                    onUpdate({ active: false, status: 'none' });
                }
            },
            (error) => {
                console.error('Recovery status listener error:', error);
                if (onError) onError(error);
            }
        );

        return unsubscribe;
    } catch (error) {
        console.error('Failed to set up recovery status listener:', error);
        if (onError) onError(error);
        return () => {};
    }
}

// Helper to get battery status string
function getBatteryStatus(level) {
    if (level < 0) return 'unknown';
    if (level <= 10) return 'critical';
    if (level <= 20) return 'low';
    if (level <= 50) return 'medium';
    return 'good';
}

/**
 * Subscribe to device notifications for FCM page (realtime listener)
 */
export function subscribeToDeviceNotifications(userId, onUpdate, onError) {
    try {
        const notificationsRef = collection(db, 'deviceNotifications');
        const q = query(
            notificationsRef,
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(30)
        );

        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const notifications = [];
                snapshot.forEach((docSnap) => {
                    const data = docSnap.data();

                    let createdAt = data.createdAt;
                    if (createdAt?.toDate) createdAt = createdAt.toDate();
                    else if (createdAt?._seconds) createdAt = new Date(createdAt._seconds * 1000);

                    notifications.push({
                        id: docSnap.id,
                        ...data,
                        createdAt
                    });
                });
                onUpdate(notifications);
            },
            (error) => {
                console.error('Realtime notifications listener error:', error);
                if (onError) onError(error);
            }
        );

        return unsubscribe;
    } catch (error) {
        console.error('Failed to set up notifications listener:', error);
        if (onError) onError(error);
        return () => {};
    }
}

export { auth, db };
